import React, { FC, useEffect, useState } from 'react';
import { Card, Tabs, Tab, Box, LinearProgress } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import axiosApiInstance from 'src/api/axiosConfig';
import DateTimeUtils from 'src/utils/datetime';
import { ScheduleToolbar } from '../ScheduleToolbar/ScheduleToolbar';
import { ScheduleCalendar } from './ScheduleCalendar';
import { useScheduleCalendarContext } from '../../common/context/CalendarContext';
import { EducationLevelGroup, Resource, EducationLevelGroupList, EducationLevel } from 'src/constants';
import { ScheduleCalendarEvent } from './ScheduleCalendar.types';

const fetchLessons = async (
    educationCenterId: number | undefined,
    hospitalId: number | undefined,
    educationLevelGroup: EducationLevelGroup,
    startDate: Date,
    endDate: Date
) => {
    const response = await axiosApiInstance.get(Resource.LESSONS, {
        params: {
            educationCenterId: educationCenterId,
            hospitalId: hospitalId,
            educationalLevels: EducationLevelGroupList[educationLevelGroup]
                .reduce<EducationLevel[]>((acc, { id }) => [...acc, id], [])
                .join(','),
            startDate: DateTimeUtils.getFormattedDate(startDate),
            endDate: DateTimeUtils.getFormattedDate(endDate)
        }
    });

    return response.data;
};

const fetchSubjects = async (): Promise<any[]> => {
    // todo: type differs from regular subject
    const body = {
        page: 0,
        size: 999,
        sort: {
            properties: ['id'],
            direction: 'ASC'
        }
    };

    try {
        const { data } = await axiosApiInstance.post('subjects', body);
        return data.content;
    } catch (e) {
        console.error(e);
        return [];
    }
};

const getCalendarEvents = async (
    educationCenterId: number | undefined,
    hospitalId: number | undefined,
    educationLevelGroup: EducationLevelGroup,
    startDate: Date,
    endDate: Date
): Promise<ScheduleCalendarEvent[]> => {
    const lessons = await fetchLessons(educationCenterId, hospitalId, educationLevelGroup, startDate, endDate);
    const subjects = await fetchSubjects();

    const calendarEvents: ScheduleCalendarEvent[] = lessons.map((lesson: any) => {
        const lessonSubject = subjects.find((subject) => subject.id === lesson.subjectId);
        // todo: would be good to get only the needed subjects from BE, not all
        return {
            start: DateTimeUtils.convertTimeToDate(lesson.startTime, new Date(lesson.lessonDate)),
            end: DateTimeUtils.convertTimeToDate(lesson.endTime, new Date(lesson.lessonDate)),
            title: lessonSubject!.name,
            educationLevels: lesson.educationLevels,
            subject: lesson.subject,
            id: lesson.id,
            children: lesson.children,
            employee: lesson.employee,
            durationMinutes: lesson.durationMinutes,
            consultationType: lesson.consultationType
        };
    });
    return calendarEvents;
};

export const TabbedScheduleCalendar: FC<any> = () => {
    const { setValue } = useFormContext();
    const { calendarConfig, calendarData, setCalendarConfig } = useScheduleCalendarContext();
    const [educationLevelGroup, setEducationLevelGroup] = useState<EducationLevelGroup>(
        calendarConfig.educationLevelGroup
    );

    const {
        isFetching: isCalendarEventsFetching,
        data: calendarEvents = [],
        refetch: refetchEvents
    } = useQuery(
        [
            calendarConfig.educationLevelGroup,
            calendarConfig.range,
            calendarConfig.viewMode,
            calendarData.educationCenter,
            calendarData.hospital,
            calendarData.newLesson,
            calendarData.deletedEvent
        ],
        () =>
            getCalendarEvents(
                calendarData.educationCenter?.id,
                calendarData.hospital?.id,
                calendarConfig.educationLevelGroup,
                calendarConfig.range.start,
                calendarConfig.range.end
            ),
        {
            keepPreviousData: true
        }
    );

    const handleChangeTab = (_: any, value: EducationLevelGroup) => {
        setEducationLevelGroup(value);
        setValue(
            'educationLevelGroup',
            EducationLevelGroupList[value].map(({ id }) => id)
        );
    };

    const viewMode = useWatch({
        name: 'viewMode',
        defaultValue: null
    });

    useEffect(() => {
        setCalendarConfig((config) => ({
            ...config,
            viewMode,
            educationLevelGroup
        }));
    }, [viewMode, educationLevelGroup]);

    return (
        <Box>
            <div style={{ minHeight: '10px' }}>
                {isCalendarEventsFetching && (
                    <LinearProgress variant="query" sx={{ '.MuiLinearProgress-bar': { transition: 'none' } }} />
                )}
            </div>

            <ScheduleToolbar />
            <Card sx={{ boxShadow: 'none' }}>
                <Tabs value={educationLevelGroup} onChange={handleChangeTab}>
                    <Tab value={EducationLevelGroup.PRESCHOOL} label="Дошкільна освіта" />
                    <Tab value={EducationLevelGroup.JUNIOR_SCHOOL} label="1-4 класи" />
                    <Tab value={EducationLevelGroup.HIGH_SCHOOL} label="5-11 класи" />
                </Tabs>
                <ScheduleCalendar
                    events={calendarEvents}
                    activeTab={educationLevelGroup}
                    refetchEvents={refetchEvents}
                />
            </Card>
        </Box>
    );
};
